/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import type Article from '../models/Article';
import ArticleDetailModal from './ArticleDetailModal';
import ArticleBookshelfDropdownModal from './ArticleBookshelfDropdownModal';
import icon from 'flarum/common/helpers/icon';
import { truncate } from 'flarum/common/utils/string';
import type Mithril from 'mithril';
import ItemList from 'flarum/common/utils/ItemList';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LogInModal from 'flarum/forum/components/LogInModal';
import Tooltip from 'flarum/common/components/Tooltip';
import extractText from 'flarum/common/utils/extractText';
import LiteraryWorkPageOrderButton from './LiteraryWorkPageOrderButton';
import WishlistRepository from '../WishlistRepository';
import ArticleNoteModal from './ArticleNoteModal';
import type { SliderItemAttrs } from '@glowingblue-orell-thalia-common/components/AbstractSlider';

const typeMapping = {
	'buch-hc': { icon: 'fas fa-book', translationKey: 'buch-hc' },
	'buch-tb': { icon: 'fas fa-book', translationKey: 'buch-tb' },
	'fremdsprachbuch-hc': { icon: 'fas fa-book', translationKey: 'fremdsprachbuch-hc' },
	'fremdsprachbuch-tb': { icon: 'fas fa-book', translationKey: 'fremdsprachbuch-tb' },
	musik: { icon: 'fas fa-music', translationKey: 'musik' },
	film: { icon: 'fas fa-film', translationKey: 'film' },
	software: { icon: 'fas fa-laptop', translationKey: 'software' },
	spiele: { icon: 'fas fa-gamepad', translationKey: 'spiele' },
	ebooks: { icon: 'fas fa-book', translationKey: 'ebooks' },
	hoerbuch: { icon: 'fas fa-headphones', translationKey: 'hoerbuch' },
	schulbuch: { icon: 'fas fa-book', translationKey: 'schulbuch' },
	papeterie: { icon: 'fas fa-pen', translationKey: 'papeterie' },
	brettspiel: { icon: 'fas fa-dice', translationKey: 'brettspiel' },
	'hoerbuch-dl': { icon: 'fas fa-headphones', translationKey: 'hoerbuch-dl' },
	elektro: { icon: 'fas fa-plug', translationKey: 'elektro' },
	'blu-ray': { icon: 'fas fa-compact-disc', translationKey: 'blu-ray' },
	kalender: { icon: 'fas fa-calendar-alt', translationKey: 'kalender' },
	wohnen: { icon: 'fas fa-couch', translationKey: 'wohnen' },
	abo: { icon: 'fas fa-newspaper', translationKey: 'abo' },
} as const satisfies Record<string, { icon: string; translationKey: string }>;

type ArticleType = keyof typeof typeMapping;

export default class ArticleSliderItem extends Component<SliderItemAttrs<Article>> {
	loading: boolean = false;

	view() {
		const { item: article, className, index } = this.attrs;
		const description = article.description() || '';
		const truncatedDescription = truncate(description, 300);
		const author = article.author();

		const isFirst = index === 0;

		return (
			<div className={`SliderItem ${className}`}>
				<div className="Banner BannerLayout--work-article">
					<div className="Banner-content">
						<div className="BannerContentContainer Article">
							{isFirst ? <h1 className="BannerTitle MainArticle">{article.title()}</h1> : <h2 className="BannerTitle">{article.title()}</h2>}
							<h3 className="BannerOverline">
								<Tooltip
									text={extractText(app.translator.trans('glowingblue-orell-articles.forum.article.search_author', { author }))}
									position="bottom"
								>
									<Link href={app.route.of_articles(author)}>{author}</Link>
								</Tooltip>
							</h3>
							<h3 className="BannerSubtitle">{article.subtitle()}</h3>
							<div className="BannerTeaser">
								<p className="ExpandableContent-body">{m.trust(truncatedDescription)}</p>
							</div>
							<div onclick={() => this.showArticleDetailModal()} className="ExpandableContent-footer">
								<span>{app.translator.trans('glowingblue-orell-articles.forum.work.read_more')}</span>
								{icon('far fa-chevron-down')}
							</div>
							<div className="ArticleActionButtons">{this.actionButtons().toArray()}</div>
							<LiteraryWorkPageOrderButton article={article} />
						</div>
						<div className="BannerContentContainer BannerImageContainer">{this.imageContainerItemList().toArray()}</div>
						<div className="BackgroundElement" style={{ '--banner-bg': '#7ea08b' }} />
					</div>
				</div>
			</div>
		);
	}

	imageContainerItemList(): ItemList<Mithril.Children> {
		const { item: article } = this.attrs;
		const articleType = article.type();
		const typeInfo = articleType in typeMapping ? typeMapping[articleType as ArticleType] : undefined;

		const items = new ItemList<Mithril.Children>();
		items.add('image', <img className="BannerImage" src={article.coverImage()} alt={article.title()} />);
		if (articleType && typeInfo) {
			items.add(
				'articleType',
				<div className="BannerArticleType-container">
					<span className="BannerArticleType">
						{app.translator.trans(`glowingblue-orell-articles.forum.article.type.${typeInfo.translationKey}`, {
							language: article.language(),
						})}
					</span>
				</div>
			);
		}

		return items;
	}

	showArticleNoteModal() {
		app.modal.show(ArticleNoteModal, { article: this.attrs.item });
	}

	showArticleDetailModal() {
		app.modal.show(ArticleDetailModal, { article: this.attrs.item });
	}

	showArticleBookshelfDropdownModal() {
		app.modal.show(ArticleBookshelfDropdownModal, { article: this.attrs.item });
	}

	actionButtons(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		if (app.initializers.has('glowingblue-orell-bookshelf')) {
			const button = (
				<Button
					className="Button Button-ArticleActionButtons"
					onclick={app.session.user ? () => this.showArticleBookshelfDropdownModal() : () => app.modal.show(LogInModal)}
				>
					<div className="ArticleActionButton-icon">{icon('far fa-books')}</div>
					<span className="ArticleActionButton-label">
						{app.translator.trans('glowingblue-orell-bookshelf.forum.article_dropdown.assign_bookshelf')}
					</span>
				</Button>
			);
			items.add('bookshelf-controls-button', button, 50);
		}

		if (app.forum.attribute<boolean>('canAddToWishlist') && this.attrs.item.productId()) {
			items.add(
				'add-to-wishlist-button',
				<Button className="Button Button-ArticleActionButtons" onclick={() => new WishlistRepository().handleClick(this.attrs.item, this)}>
					<div className="ArticleActionButton-icon">{icon('fas fa-bookmark')}</div>
					<span className="ArticleActionButton-label">{app.translator.trans('glowingblue-orell-articles.forum.work.article.wishlist')}</span>
				</Button>,
				30
			);
		}

		return items;
	}
}
